import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';
import { concatMap, EMPTY, map, Observable, of, withLatestFrom } from 'rxjs';

import {
  applyPropsPatch,
  CanvasElement,
  CanvasElementId,
  CanvasElementType,
  ElementTargetPath,
  findByRefIds,
  HeightSettings,
  PartialPropsOf,
  setWrapperRefMutably,
  WrapperRefs,
} from '@blaze-runtime/engine-domain';
import {
  DataBindingSourceInfo,
  FormListModelField,
  FormListPregenerationType,
  getActiveBinding,
  mergeColumns,
  MergeColumnsConfig,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetColumn } from '@blaze-shared/schema-domain';
import { deepmergeExceptArrays, toResolvedColumnName } from '@blaze-shared/shared-util';
import { StyleSheet } from '@blaze-shared/theming-domain';
import { setValueByPath } from '@blaze-shared/workflow-domain';

import { RuntimeFacade } from '../+state';
import {
  filterOutInternalColumns,
  getColumnsFromSchema,
} from '../canvas-elements/element-model/data-binding';
import {
  arePropsWithData,
  hasDataProps,
  hasProps,
  hasWrapperRefs,
} from '../canvas-elements/element-model/element-props-guards';
import {
  isFormDetailsList,
  isFormList,
  isFormListLike,
} from '../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../canvas-elements/element-model/wrapper-refs';
import {
  DropdownPropsModel,
  FORM_LIST_ACTIONS_SLOT_ID,
  FORM_LIST_ROW_TEMPLATE_SLOT_ID,
  FormListElement,
  FormListLikeElement,
  TagsPropsModel,
  TextInputPropsModel,
} from '../elements';
import { FormFieldGenerationParams } from '../elements/__base/generation';
import { transformElementTypeMutably } from '../utils/canvas-element/transform';
import {
  cleanRemovedFieldsMutably,
  columnsToPregenerationData,
  COMMIT_BUTTON_REFID,
  CreateCanvasElementModel,
  FindFieldTargetPathData,
  getColumnContainer,
  getLastGeneratedElementPath,
  getNewFormFieldVisibility,
  prepareFieldElements,
  updateGenerationAndModelMismatchesMutably,
} from '../utils/elements/generation.utils';
import { getPathFirstInSlot, getPathLastInSlot } from '../utils/layout/helpers';
import { placeElementMutably } from '../utils/layout/place-element';

import { ElementDefinitionsService } from './element-definitions.service';
import { ElementTransformationService } from './element-transformation.service';
import { makeStyleConfigPropsForDefaultStyleSheet } from './styles';

export interface FormListGenerationData {
  /** Used to pass the id if the list already exists */
  listId?: CanvasElementId;
  /** Used for initial creation of the formList inside of a page or dialog */
  listTargetPath?: ElementTargetPath;
  pregenerationType?: FormListPregenerationType;
  /**
   * The input source used to generate the formList
   *
   * **Note:** the data source can be different than the actual data source bound to the element (for example for 'insert'/'update' actions, which use the primary data source instead of full schema)
   */
  inputDataSource?: DataBindingSourceInfo;
  isNewDataSource?: boolean;
  /**
   * If falsy - the formList's schema and data source will be refreshed (data source selection/refresh);
   * If true, the schema and columns will not be updated (field modification scenario)
   */
  keepSchema?: boolean;
  overrideColumnVisibility?: Record<string, boolean>;

  heightSettings?: HeightSettings;
  accountsTableName?: string;
}

type FormListGenerationSchemaType = 'fullSchema' | 'keep';

const FORM_LIST_MODEL_MERGE_CONFIG: MergeColumnsConfig<FormListModelField, 'fieldId', 'field'> = {
  keyId: 'fieldId',
  keyName: 'field',
  fieldsToUpdate: ['columnType', 'dataType'],
  mapColumnFn: datasetColumnToFormField,
  initAppendedColumnFn,
};

@Injectable({ providedIn: 'root' })
export class FormListGenerationService {
  private readonly elementDefinitions = inject(ElementDefinitionsService);
  private readonly elementTransformationService = inject(ElementTransformationService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);

  /**
   * Prepares and preloads schemas and returns the FormListGenerationData object.
   *
   * Use this step to prepare the data for the {@link regenerateFormMutably} method.
   *
   * @param element if formList exists already, its properties will be used to discover the settings
   * @param generationData a partial FormListGenerationData object used to provide initial/or forced values when generating/regenerating
   * @param elementDataSource the data source which is the base for pregeneration mechanism (either workflow or dataset)
   * - full schema will be used as the data source used for the formList elements generation
   * @returns an Observable which emits the formList generation data once all needed schemas are ready
   */
  prepareGenerationDataAndPreloadSchemas(
    element: FormListLikeElement,
    generationData?: Partial<FormListGenerationData>,
    elementDataSource?: DataBindingSourceInfo,
  ): Observable<FormListGenerationData> {
    // Prepare formList schemas based on action and element data source
    let elementValueDataSource =
      elementDataSource || getActiveBinding(element.props.data, 'value')?.dataSource;
    if (!elementValueDataSource) {
      console.error('Element data source missing');
      return EMPTY;
    }

    if (elementValueDataSource.sourceType === 'mirror') {
      const targetElement = this.runtimeFacade.elementsValue[elementValueDataSource.id];
      if (!targetElement || !hasDataProps(targetElement)) {
        throw new Error('target element missing or invalid');
      }
      elementValueDataSource = getActiveBinding(targetElement.props.data, 'value')?.dataSource;
      if (!elementValueDataSource) {
        throw new Error('target element datasource missing or invalid');
      }
    }
    const pregenerationType =
      generationData?.pregenerationType ?? element?.props.content.pregenerationType;
    const schemaType = getSchemaType(generationData);

    return this.prepareDataSource(elementValueDataSource, schemaType).pipe(
      withLatestFrom(this.schemaService.obtainAccountsTableName()),
      map(
        ([inputDataSource, accountsTableName]) =>
          ({
            ...generationData,
            listId: element?.id,
            inputDataSource,
            accountsTableName,
            pregenerationType,
          }) as FormListGenerationData,
      ),
    );
  }

  regenerateMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    data: FormListGenerationData,
  ): {
    listId: CanvasElementId;
    commitButtonId: CanvasElementId | undefined;
    removedElementIds: string[];
  } {
    let formListElement = data.listId ? entityState.entities[data.listId] : undefined;

    // List already present and the type is incorrect - throw error
    if (!!formListElement && !isFormListLike(formListElement)) {
      throw new Error('Incorrect element type');
    }

    // Create or update list
    const { listId } = this.addOrUpdateFormListMutably(
      entityState,
      styleSheets,
      formListElement,
      data,
    );
    // Reassign the list so the new or up-to-date version is referenced.
    formListElement = entityState.entities[listId];
    if (!isFormListLike(formListElement)) {
      throw new Error('List element error');
    }
    if (!hasWrapperRefs(formListElement)) {
      setValueByPath(formListElement.props, 'wrapper.refs', {});
      if (!hasWrapperRefs(formListElement)) {
        throw new Error('Wrapper refs missing error');
      }
    }
    const refs = getWrapperRefs(formListElement);
    const model = formListElement?.props?.content?.model;

    const pregenerationData = columnsToPregenerationData(
      data.inputDataSource?.columns || [],
      model,
      {
        isFormReadonly: data.pregenerationType === 'view', // emptyish is legacy = edit
        isInsertForm: true,
        overrideColumnVisibility: data.overrideColumnVisibility,
        includedLimit: data.isNewDataSource ? 3 : undefined,
        fieldsLabelGen: formListElement?.props?.featuresConfig?.fieldsLabelGen,
      },
    );

    updateGenerationAndModelMismatchesMutably(model, pregenerationData);

    // Remove tracked elements for fields that are no longer needed
    const removedElementIds = cleanRemovedFieldsMutably(entityState, pregenerationData, refs);

    // Add or update input fields
    let hasFields = false;
    if (data.inputDataSource) {
      const fieldsCreationData = prepareFieldElements(
        formListElement.id,
        pregenerationData,
        data.inputDataSource.id,
        'editableList',
        false,
        this.schemaService.getSchema.bind(this.schemaService),
        this.elementDefinitions.getDefinitionSafe.bind(this.elementDefinitions),
        this.initializeGeneratedFormFieldProps.bind(this),
        entityState,
        refs,
        styleSheets,
      ).map((field) => {
        const { title } = (field.initProps as any).content;
        title.enabled = false;
        title.position = isFormList(formListElement) ? 'top' : 'left';

        switch (field.elementDefinition.type) {
          case 'dropdown': {
            const { content } = field.initProps as DropdownPropsModel;
            setValueByPath(content, 'menu.placeholder', content.title.label);
            break;
          }
          case 'tags': {
            const { content } = field.initProps as TagsPropsModel;
            setValueByPath(content, 'tags.placeholder', content.title.label);
            break;
          }
          case 'textField':
          case 'textInput': {
            const { content } = field.initProps as TextInputPropsModel;
            setValueByPath(content, 'input.placeholder', content.title.label);
            break;
          }
          default:
            break;
        }

        return field;
      });

      const isInitialGeneration = !data.listId;
      hasFields = fieldsCreationData.length > 0;

      const findFieldTargetPathData: FindFieldTargetPathData<FormListLikeElement> = {
        element: formListElement,
        isInitialGeneration,
        totalFields: fieldsCreationData.length,
        fieldIndex: 0,
      };

      // Add or update each element
      for (let i = 0; i < fieldsCreationData.length; i++) {
        const fieldData = fieldsCreationData[i];
        findFieldTargetPathData.fieldIndex = i;
        this.addOrUpdateFieldMutably(entityState, refs, fieldData, findFieldTargetPathData);
      }

      // TODO: Reorder elements
    }

    let commitButtonId: CanvasElementId | undefined = undefined;

    if (isFormList(formListElement)) {
      // Create or update commit button if needed
      const isCommitButtonNeeded =
        hasFields &&
        formListElement.props.content.origin !== 'form-group' &&
        formListElement.props.content.origin !== 'wizard';
      if (isCommitButtonNeeded) {
        const buttonId: CanvasElementId | undefined = refs[COMMIT_BUTTON_REFID];
        ({ commitButtonId } = this.addOrUpdateCommitButtonMutably(
          entityState,
          styleSheets,
          formListElement,
          buttonId,
        ));
        refs[COMMIT_BUTTON_REFID] = commitButtonId;
      }
    }

    return { listId, commitButtonId, removedElementIds };
  }

  /**
   * Prepares the formList schema (obtains the latest known schemas) and also obtains all nested link schemas
   * @param elementValueDataSource the data source info used to obtain schemas
   * @param schemaType prepares the schema based on the full schema
   * @returns Observable emitting the formList schema
   */
  private prepareDataSource(
    elementValueDataSource: DataBindingSourceInfo,
    schemaType: FormListGenerationSchemaType,
  ): Observable<DataBindingSourceInfo> {
    // If the data source should not be updated/refreshed - keep the previous one
    if (schemaType === 'keep') {
      return of(elementValueDataSource);
    }

    const isWorkflow = elementValueDataSource.sourceType === 'context';
    // Step 1 - prepare formList schema
    const elementWfSchema$ = this.schemaService.obtainSchema(elementValueDataSource.id, isWorkflow);
    // Step 2 - Obtain nested schemas (link columns); emit the formList schema as the result
    return elementWfSchema$.pipe(
      // Ensure schemas for linked columns are fetched and ready (this is needed inside of the generation mechanism)
      concatMap((elementSchema) => {
        if (!elementSchema) {
          throw new Error('Could not fetch formList schema');
        }

        // After obtaining all nested schemas -> emit the elementSchema
        return this.schemaService.obtainNestedSchemas(elementSchema).pipe(
          map(() => ({
            ...elementValueDataSource,
            sourceType: elementValueDataSource.sourceType,
            columns: filterOutInternalColumns(getColumnsFromSchema(elementSchema)),
            // Provide original workflow id if workflow is used, otherwise pass the resolved dataset id
            id: isWorkflow ? elementValueDataSource.id : elementSchema.id,
          })),
        );
      }),
    );
  }

  private initializeGeneratedFormFieldProps<T extends CanvasElementType>(
    type: T,
    params: FormFieldGenerationParams<T>,
    styleSheets: StyleSheet[],
  ): PartialPropsOf<T> | undefined {
    const definition = this.elementDefinitions.getDefinition(type);
    if (!definition) {
      return;
    }

    const props = definition.generation?.formField?.({ ...params }) ?? params.props;
    const result = {
      ...props,
      ...makeStyleConfigPropsForDefaultStyleSheet(styleSheets, type),
    };
    return result;
  }

  /** Adds or updates the formList */
  /* Generating a pregenerationData object that will be used to generate the code for the formList. */
  private addOrUpdateFormListMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    listElement: FormListLikeElement | undefined,
    {
      listTargetPath,
      inputDataSource,
      isNewDataSource,
      accountsTableName,
      pregenerationType,
    }: FormListGenerationData,
  ): { listId: CanvasElementId } {
    // Generate formList props
    const listProps = this.generateFormListProps(
      listElement,
      inputDataSource,
      isNewDataSource,
      accountsTableName,
    );
    const elementDefinition = this.elementDefinitions.getDefinitionSafe(
      pregenerationType === 'edit' ? 'editableList' : 'formDetailsList',
    );

    if (!listElement) {
      if (!listTargetPath) {
        throw new Error(`Could not create ${elementDefinition.name} - target path not provided`);
      }

      const result = placeElementMutably(entityState, {
        elementDefinition,
        atPath: listTargetPath,
        initProps: listProps,
        style: { styleSheets, useDefault: true },
      });
      if (!result?.elementId) {
        throw new Error(`Adding ${elementDefinition.name} failed`);
      }

      return { listId: result.elementId };
    }

    const list = entityState.entities[listElement.id];
    if (!hasProps(list)) {
      throw new Error(`${elementDefinition.name} element error`);
    }
    list.props = deepmergeExceptArrays(list.props as any, listProps as any);
    if (arePropsWithData(listProps)) {
      (list.props as any).data = listProps.data;
    }

    return { listId: listElement.id };
  }

  /**
   * Add or update field in the formList
   * @param entityState the entity state
   * @param refs
   * @param fieldData
   * @param findFieldTargetPathData data used to find the target path for the new element
   */
  private addOrUpdateFieldMutably(
    entityState: EntityState<CanvasElement>,
    refs: WrapperRefs,
    fieldData: CreateCanvasElementModel,
    findFieldTargetPathData: FindFieldTargetPathData<FormListLikeElement>,
  ): void {
    let elementId: CanvasElementId | undefined = findByRefIds(refs, fieldData.refIds);
    if (!elementId) {
      // If element missing - create element
      const fieldTargetPath = getFieldTargetPath(entityState, findFieldTargetPathData);
      const result = placeElementMutably(entityState, {
        elementDefinition: fieldData.elementDefinition,
        atPath: fieldTargetPath,
        initProps: fieldData.initProps,
      });
      if (!result?.elementId) {
        throw new Error(`Field element creation error`);
      }

      // Store the reference
      elementId = result.elementId;
      setWrapperRefMutably(refs, fieldData.refIds, elementId);
    }

    if (!elementId) {
      throw new Error('Field element error');
    }
    const element = entityState.entities[elementId];
    if (!element) {
      throw new Error('Field element error');
    }

    // Type changed
    if (element.type !== fieldData.elementDefinition.type) {
      const transformationData = this.elementTransformationService.getTransformation(
        element.type,
        fieldData.elementDefinition.type,
      );

      const elementTransformFn =
        transformationData?.transformElementFn ?? ((element): CanvasElement => element);

      if (!transformationData?.transformElementFn) {
        console.warn(
          `Transformation data missing for ${element.type}->${fieldData.elementDefinition.type}`,
        );
      }

      transformElementTypeMutably(
        entityState,
        element.id,
        fieldData.elementDefinition,
        elementTransformFn,
        fieldData.initProps,
        fieldData.updateProps,
      );
    }

    if (element.height?.fixed) {
      element.height.fixed = false;
    }

    // Update props
    if (hasProps(element) && fieldData.updateProps) {
      element.props = deepmergeExceptArrays(element.props as any, fieldData.updateProps as any);
    }
  }

  /** Adds or updates the commit button */
  private addOrUpdateCommitButtonMutably(
    entityState: EntityState<CanvasElement>,
    styleSheets: StyleSheet[],
    list: FormListElement,
    buttonId: CanvasElementId | undefined,
  ): { commitButtonId: CanvasElementId } {
    if (buttonId) {
      return { commitButtonId: buttonId };
    }

    const elementDefinition = this.elementDefinitions.getDefinitionSafe('button');
    elementDefinition.name = 'Commit button';

    const targetPath = getPathLastInSlot(entityState, list.id, FORM_LIST_ACTIONS_SLOT_ID);

    const initProps = applyPropsPatch(elementDefinition.initial, {
      content: {
        button: { label: 'Save' },
      },
    });

    const result = placeElementMutably(entityState, {
      elementDefinition,
      atPath: targetPath,
      initProps,
      style: { styleSheets, useDefault: true },
    });
    if (!result?.elementId) {
      throw new Error('Error when placing commit button');
    }

    return { commitButtonId: result.elementId };
  }

  private generateFormListProps(
    listElement: FormListLikeElement | undefined,
    inputDataSource: DataBindingSourceInfo | undefined,
    isNewDataSource: boolean | undefined,
    accountsTableName: string | undefined,
  ): PartialPropsOf<'editableList'> {
    const initProps: PartialPropsOf<'editableList'> = {
      content: {},
      wrapper: { refs: {} },
    };

    // If new data source info missing, do not add data related props
    if (!inputDataSource) {
      return initProps;
    }

    if (!inputDataSource.id) {
      throw new Error('Data source missing');
    }

    // Update model/fields
    if (initProps.content) {
      // Pregenerated list initialization (separate page or dialog)
      // Missing list element means that it's being created right now through pregeneration (not by dragging)
      if (!listElement) {
        // Set origin as generated
        initProps.content.origin = 'generated';
      }

      initProps.content.model = mergeColumns(
        { ...FORM_LIST_MODEL_MERGE_CONFIG, accountsTableName },
        inputDataSource.columns,
        listElement?.props?.content?.model,
        isNewDataSource || false,
      );

      if (isFormDetailsList(listElement)) {
        initProps.content.options ??= {};
        initProps.content.options.noDataMessage = 'Not provided';
      }
    }

    // Prepare data bindings for initial list pregeneration
    // TODO: think whether this is needed - in new pregeneration -> we populate the data source
    // const previousDataBinding = hasDataProps(listElement) ? listElement?.props?.data?.find(d => d.key === 'value') : undefined;
    if (!listElement) {
      initProps.data = [
        {
          enabled: true,
          key: 'value',
          dataSource: { ...inputDataSource },
          bindings: {},
        },
      ];
    }

    return initProps;
  }
}

function getSchemaType(
  generationData: Partial<FormListGenerationData> | undefined,
): FormListGenerationSchemaType {
  if (generationData?.keepSchema === true) {
    return 'keep';
  }
  return 'fullSchema';
}

function datasetColumnToFormField(
  col: DatasetColumn,
  accountsTableName: string | undefined,
  fieldsLabelGen: number | undefined,
): FormListModelField {
  return {
    fieldId: col.id,
    field: col.name,
    title: toResolvedColumnName(col.name, fieldsLabelGen),
    value: null,
    dataType: col.dataType,
    controlType: '',
    columnType: col.columnType,
    // if account col => hide the field by default, otherwise let further logic decide
    visible: getNewFormFieldVisibility(col, accountsTableName),
  };
}

function initAppendedColumnFn(column: FormListModelField): FormListModelField {
  return { ...column, visible: false };
}

/**
 * Find the target path/where to put the field.
 * It can be either the formList element itself or the column container
 * if there are many elements and they need to be spread into multiple columns.
 */
function getFieldTargetPath(
  entityState: EntityState<CanvasElement>,
  data: FindFieldTargetPathData<FormListLikeElement>,
): ElementTargetPath {
  const columnContainer = getColumnContainer(entityState.entities, data.element);
  const parentElement = columnContainer ?? data.element; // If present - use column container to be the parent for the field
  const parentSlotId = FORM_LIST_ROW_TEMPLATE_SLOT_ID;

  let fieldTargetPath = getLastGeneratedElementPath(
    entityState.entities,
    data.element,
    parentElement.slots[parentSlotId],
  );
  if (fieldTargetPath) {
    // If last generated element found
    // The new field should be placed after the already existing one
    if (fieldTargetPath.index !== undefined) {
      fieldTargetPath.index++;
    }
    fieldTargetPath.targetPosition = { type: 'right' };
  } else if (columnContainer) {
    // If this is the first generated element in a column
    // Place the first generated element as last in column
    fieldTargetPath = getPathLastInSlot(entityState, parentElement.id, parentSlotId);
  } else {
    // If no columns - place the first generated element at the top (as it also contains the button, which should remain below by default)
    fieldTargetPath = getPathFirstInSlot(entityState, parentElement.id, parentSlotId);
  }

  return fieldTargetPath;
}
